/**
 * Operator-facing validation for "Add by IP address" on an ESC/POS printer.
 *
 * Explains, before anything leaves the renderer, why an address cannot be a
 * printer. It is not the security boundary — the shell enforces the same rule
 * again (`electron/main/services/escpos-lan/destination.ts`).
 */

/** The factory-default raw-print port on nearly every network receipt printer. */
export const ESCPOS_DEFAULT_PORT = 9100;
const MIN_PORT = 1024;
const MAX_PORT = 65535;

export type EscPosAddressCheck =
  | { readonly ok: true; readonly ipAddress: string; readonly port: number }
  | { readonly ok: false; readonly message: string };

const IPV4_OCTET = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

function octets(host: string): number[] | null {
  const parts = host.split('.');
  if (parts.length !== 4 || parts.some((part) => !IPV4_OCTET.test(part))) return null;
  return parts.map((part) => Number.parseInt(part, 10));
}

function isPrivate([a, b]: number[]): boolean {
  if (a === 10) return true;
  if (a === 172 && b !== undefined && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 169 && b === 254) return true;
  return false;
}

export function checkEscPosPort(input: string | number): { ok: true; port: number } | { ok: false; message: string } {
  const port = typeof input === 'number' ? input : Number.parseInt(String(input).trim(), 10);
  if (!Number.isInteger(port) || port < MIN_PORT || port > MAX_PORT) {
    return {
      ok: false,
      message: `Enter a port between ${String(MIN_PORT)} and ${String(MAX_PORT)}. Most printers use ${String(ESCPOS_DEFAULT_PORT)}.`,
    };
  }
  return { ok: true, port };
}

/** `input` may carry its own `:port`, which wins over `defaultPort`. */
export function checkEscPosAddress(input: string, defaultPort: string | number): EscPosAddressCheck {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ok: false, message: 'Enter the IP address from the printer’s self-test page or network settings.' };
  }

  const [host = '', portPart, ...rest] = trimmed.split(':');
  if (rest.length > 0) return { ok: false, message: 'That does not look like an IPv4 address.' };

  const parsed = octets(host);
  if (parsed === null) {
    return /[a-z]/i.test(host)
      ? {
          ok: false,
          message: 'Enter the printer’s IP address (for example 192.168.1.50), not its name.',
        }
      : { ok: false, message: 'That does not look like an IPv4 address, for example 192.168.1.50.' };
  }

  if (!isPrivate(parsed)) {
    return {
      ok: false,
      message:
        'Printers can only be added on this shop’s private network (addresses such as 192.168.x.x, 10.x.x.x or 172.16–31.x.x).',
    };
  }

  const port = checkEscPosPort(portPart ?? defaultPort);
  if (!port.ok) return port;
  return { ok: true, ipAddress: host, port: port.port };
}
